import i18n from "i18next";
import { initReactI18next } from "react-i18next";

type Service = {
  title: string;
  description: string;
};

type Commitment = {
  title: string;
  description: string;
  icon: string;
};

type Stat = {
  label: string;
  value: string;
};

const resources = {
  pt: {
    translation: {
      nav: {
        about: "Sobre",
        services: "Serviços",
        quality: "Excelência",
        contact: "Contato",
      },
      hero: {
        tag: "Trading Wellness",
        headline: "Conectando beleza, bem-estar e performance entre Brasil e Estados Unidos",
        subheadline:
          "Importação e exportação de beleza e performance com segurança, especialistas locais e logística inteligente nos dois países.",
        button: "Conheça a BioVita",
        compliance: "FDA · ANVISA · MoCRA",
      },
      about: {
        title: "BioVita, presença binacional em beleza, saúde e performance",
        paragraphOne:
          "A BioVita é uma companhia americana estabelecida em Orlando, Florida, que atua nos fluxos de importação e exportação de cosméticos, dermocosméticos e suplementos alimentares entre Estados Unidos e Brasil.",
        paragraphTwo:
          "Nossa equipe integra especialistas em regulação sanitária, supply chain e desenvolvimento comercial para que marcas internacionais escalem nos dois maiores mercados das Américas com segurança, velocidade e governança.",
        missionTitle: "Missão BioVita",
        missionText:
          "Impulsionar marcas globais de beleza e bem-estar, conectando consumidores e varejistas em ambos os países com operações binacionais fluidas e compliance irrestrito.",
        chips: ["Orlando · Florida", "São Paulo · Brasil", "Operações bilaterais"],
        stats: [
          { value: "14+", label: "Anos em comércio exterior" },
          { value: "52", label: "Marcas e laboratórios parceiros" },
          { value: "2", label: "Hubs logísticos integrados" },
        ] as Stat[],
      },
      services: {
        introTitle: "Nossas frentes de atuação",
        introDescription:
          "Estruturamos soluções customizadas para marcas de beleza, nutrição e alta performance que necessitam operar em conformidade máxima, com agilidade na cadeia logística e expansão comercial consistente.",
        cards: [
          {
            title: "Importação para o Brasil",
            description:
              "Registro ANVISA, adaptação de rotulagem em português, planejamento tributário e distribuição nacional para redes varejistas e clínicas especializadas.",
            flag: "BR",
            icon: "M3 8h18M3 16h18M9 4h6a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4Z",
          },
          {
            title: "Exportação para os EUA",
            description:
              "Suporte completo para marcas brasileiras no mercado americano com registros FDA, adequação ao MoCRA, fulfilment e parcerias de retail & e-commerce.",
            flag: "US",
            icon: "M3 7h18M3 12h18M3 17h18M7 4h4v6H3V8a4 4 0 0 1 4-4Zm10 0a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4h-4V4h4Z",
          },
          {
            title: "Logística e inovação",
            description:
              "Gestão integrada de containers, cold chain, fulfillment omnichannel e soluções digitais de rastreabilidade com dashboards executivos em tempo real.",
            icon: "M3 7h18v6H3V7Zm2 6v8h14v-8M6 18h12M8 10h3m2 0h3",
          },
        ] as Service[],
        highlight: "Operação 24/7 em ambos os hemisférios",
      },
      commitments: {
        title: "Excelência e governança que ampliam confiança",
        description:
          "Operamos com due diligence contínua, contratos auditáveis e KPIs que garantem transparência financeira, sustentabilidade e segurança sanitária em cada etapa da cadeia.",
        items: [
          {
            title: "Compliance integral",
            description:
              "Gestão regulatória FDA, ANVISA, USDA e órgãos estaduais, com monitoramento constante de lotes e documentação digital aprovada.",
            icon: "shield-check",
          },
          {
            title: "Solidez financeira",
            description:
              "Relacionamento com bancos internacionais, seguros de carga dedicados e políticas rígidas de governança ESG.",
            icon: "banknote",
          },
          {
            title: "Rede global",
            description:
              "Laboratórios certificados, distribuidores premium e operadores logísticos especialistas em beleza e wellness.",
            icon: "globe-2",
          },
          {
            title: "Inovação sustentável",
            description:
              "Programas de economia circular, embalagens eco design e rastreabilidade de carbono em toda a jornada.",
            icon: "leaf",
          },
        ] as Commitment[],
      },
      contact: {
        title: "Converse com a BioVita",
        description:
          "Bancos, investidores, marcas e órgãos reguladores têm atendimento dedicado. Envie sua mensagem para desenharmos a melhor estratégia para a sua operação.",
        addressLabel: "Endereço corporativo",
        addressValue: "BioVita USA – 8600 Commodity Cir. 139, Orlando, FL 32819",
        emailLabel: "E-mail",
        form: {
          name: "Nome",
          company: "Empresa",
          email: "E-mail",
          message: "Mensagem",
          namePlaceholder: "Nome completo",
          companyPlaceholder: "Instituição ou marca",
          emailPlaceholder: "contact@biovitacbd.com",
          messagePlaceholder: "Conte-nos sobre sua demanda binacional",
          submit: "Enviar mensagem",
          privacy: "A BioVita segue padrões rigorosos de confidencialidade e proteção de dados corporativos.",
        },
      },
      footer: {
        legal: "© {{year}} BioVita Global Wellness Trade LLC. Todos os direitos reservados.",
        badges: ["FDA · MoCRA 2024", "ANVISA · RDC 752", "Membro SelectUSA"],
      },
      translationToggle: {
        label: "Idioma",
        portuguese: "Português",
        english: "English",
      },
      complianceTicker: ["Operações binacionais", "Cosméticos & Dermocosméticos", "Suplementos premium", "Performance e bem-estar"],
    },
  },
  en: {
    translation: {
      nav: {
        about: "About",
        services: "Services",
        quality: "Excellence",
        contact: "Contact",
      },
      hero: {
        tag: "Trading Wellness",
        headline: "Connecting beauty, wellness and performance between Brazil and the United States",
        subheadline:
          "Bilateral beauty and performance trading with expert compliance, local teams and smart logistics across both markets.",
        button: "Discover BioVita",
        compliance: "FDA · ANVISA · MoCRA",
      },
      about: {
        title: "BioVita, binational presence in beauty, health and performance",
        paragraphOne:
          "BioVita is an American company based in Orlando, Florida, operating import and export flows for cosmetics, dermocosmetics and dietary supplements between the United States and Brazil.",
        paragraphTwo:
          "Our team brings together regulatory experts, supply chain strategists and business developers so international brands can scale in both markets with safety, agility and governance.",
        missionTitle: "BioVita Mission",
        missionText:
          "Empower global beauty and wellness brands by connecting consumers and retailers in both countries through seamless bilateral operations and uncompromising compliance.",
        chips: ["Orlando · Florida", "São Paulo · Brazil", "Bilateral operations"],
        stats: [
          { value: "14+", label: "Years in foreign trade" },
          { value: "52", label: "Partner brands and labs" },
          { value: "2", label: "Integrated logistics hubs" },
        ] as Stat[],
      },
      services: {
        introTitle: "Our areas of expertise",
        introDescription:
          "We design bespoke solutions for beauty, nutrition and high-performance brands that demand maximum compliance, agile supply chains and consistent commercial expansion.",
        cards: [
          {
            title: "Import to Brazil",
            description:
              "ANVISA registration, Portuguese labeling, tax planning and nationwide distribution for retailers and professional clinics.",
          },
          {
            title: "Export to the USA",
            description:
              "Full support for Brazilian brands in the U.S. market with FDA registration, MoCRA alignment, fulfilment and retail & e-commerce partnerships.",
          },
          {
            title: "Logistics & innovation",
            description:
              "Integrated management of containers, cold chain, omnichannel fulfilment and digital traceability solutions with real-time executive dashboards.",
          },
        ] as Service[],
        highlight: "24/7 operation in both hemispheres",
      },
      commitments: {
        title: "Excellence and governance that build trust",
        description:
          "We run continuous due diligence, auditable contracts and KPIs that ensure financial transparency, sustainability and sanitary safety across the entire chain.",
        items: [
          {
            title: "Total compliance",
            description:
              "Regulatory management for FDA, ANVISA, USDA and state agencies with constant batch monitoring and approved digital documentation.",
            icon: "shield-check",
          },
          {
            title: "Financial strength",
            description:
              "Relationships with international banks, dedicated cargo insurance and strict ESG governance policies.",
            icon: "banknote",
          },
          {
            title: "Global network",
            description:
              "Certified laboratories, premium distributors and logistics operators specialized in beauty and wellness.",
            icon: "globe-2",
          },
          {
            title: "Sustainable innovation",
            description:
              "Circular economy programs, eco-designed packaging and carbon traceability throughout the journey.",
            icon: "leaf",
          },
        ] as Commitment[],
      },
      contact: {
        title: "Talk to BioVita",
        description:
          "Banks, investors, brands and regulators receive dedicated support. Send us a message so we can design the best strategy for your operation.",
        addressLabel: "Corporate address",
        addressValue: "BioVita USA – 8600 Commodity Cir. 139, Orlando, FL 32819",
        emailLabel: "Email",
        form: {
          name: "Name",
          company: "Company",
          email: "Email",
          message: "Message",
          namePlaceholder: "Full name",
          companyPlaceholder: "Institution or brand",
          emailPlaceholder: "contact@biovitacbd.com",
          messagePlaceholder: "Tell us about your bilateral demand",
          submit: "Send message",
          privacy: "BioVita follows strict confidentiality and corporate data protection policies.",
        },
      },
      footer: {
        legal: "© {{year}} BioVita Global Wellness Trade LLC. All rights reserved.",
        badges: ["FDA · MoCRA 2024", "ANVISA · RDC 752", "SelectUSA Member"],
      },
      translationToggle: {
        label: "Language",
        portuguese: "Português",
        english: "English",
      },
      complianceTicker: [
        "Bilateral operations",
        "Cosmetics & Dermocosmetics",
        "Premium supplements",
        "Performance & wellness",
      ],
    },
  },
};

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    lng: "pt",
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });
}

export { resources };
export type { Service, Commitment, Stat };
export default i18n;
